import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import marketplaceService from '../../../services/marketplaceService';
import { auctionAlertService } from '../../../services/auctionAlertService';
import auctionEmailService from '../../../services/auctionEmailService';
import liveNotificationService from '../../../services/liveNotificationService';
import { supabase } from '../../../lib/supabase';
import LiveBidFeed from './LiveBidFeed';

const AuctionFloorDetail = ({ auction, onClose, onBidPlaced }) => {
  const { user } = useAuth();
  const [bidAmount, setBidAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bids, setBids] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [currentAuction, setCurrentAuction] = useState(auction);
  const [newBidFlash, setNewBidFlash] = useState(false);
  const [bidError, setBidError] = useState('');
  const [bidSuccess, setBidSuccess] = useState('');
  const channelRef = useRef(null);

  const isOwnAuction = currentAuction?.seller_id === user?.id;
  const minBid = parseFloat(currentAuction?.current_bid || currentAuction?.starting_bid || 0) + parseFloat(currentAuction?.bid_increment || 10);
  const isEnded = new Date(currentAuction?.end_date) <= new Date();

  const updateTimeRemaining = () => {
    const now = new Date();
    const end = new Date(currentAuction?.end_date);
    const diff = end - now;
    if (diff <= 0) { setTimeRemaining('Auction Ended'); return; }
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    if (days > 0) setTimeRemaining(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    else if (hours > 0) setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
    else setTimeRemaining(`${minutes}m ${seconds}s`);
  };

  const loadBids = async () => {
    const { data } = await marketplaceService?.getAuctionBids(currentAuction?.id);
    setBids(data || []);
  };

  const refreshAuction = async () => {
    const { data } = await marketplaceService?.getAuctionById(currentAuction?.id);
    if (data) setCurrentAuction(data);
  };

  useEffect(() => {
    loadBids();
    const timer = setInterval(updateTimeRemaining, 1000);
    updateTimeRemaining();
    return () => clearInterval(timer);
  }, [currentAuction?.id]);

  // Real-time subscription for auction bids
  useEffect(() => {
    if (!currentAuction?.id || !supabase) return;

    // Clean up any existing channel first
    if (channelRef?.current) {
      supabase?.removeChannel(channelRef?.current);
      channelRef.current = null;
    }

    const channelName = `auction_bids_floor:${currentAuction?.id}:${Date.now()}`;

    const channel = supabase?.channel(channelName);

    channel?.on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'auction_bids',
      filter: `auction_id=eq.${currentAuction?.id}`
    }, () => {
      setNewBidFlash(true);
      setTimeout(() => setNewBidFlash(false), 1500);
      loadBids();
      refreshAuction();
    });

    channel?.subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef?.current) {
        supabase?.removeChannel(channelRef?.current);
        channelRef.current = null;
      }
    };
  }, [currentAuction?.id]);

  const handlePlaceBid = async (e) => {
    e?.preventDefault();
    setBidError('');
    setBidSuccess('');

    const amount = parseFloat(bidAmount);
    if (isNaN(amount) || amount < minBid) {
      setBidError(`Minimum bid is $${minBid?.toFixed(2)}`);
      return;
    }

    setSubmitting(true);

    const bidData = {
      auction_id: currentAuction?.id,
      bidder_id: user?.id,
      bid_amount: amount,
      currency: currentAuction?.currency || 'USD'
    };

    const { data, error } = await marketplaceService?.placeBid(bidData);

    if (!error) {
      const auctionTitle = currentAuction?.title;
      const previousHighBidder = bids?.[0]?.bidder_id;

      if (previousHighBidder && previousHighBidder !== user?.id) {
        auctionAlertService?.sendOutbidAlert(previousHighBidder, {
          id: currentAuction?.id,
          title: auctionTitle,
          currentBid: amount,
          yourBid: parseFloat(bids?.[0]?.bid_amount || 0)
        });
        liveNotificationService?.createNotification({
          userId: previousHighBidder,
          notificationType: 'outbid',
          title: "You've been outbid!",
          message: `Someone placed a higher bid of $${amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })} on "${auctionTitle}".`,
          auctionId: currentAuction?.id,
          metadata: { currentBid: amount, previousBid: parseFloat(bids?.[0]?.bid_amount || 0) }
        });
        auctionEmailService?.sendOutbidEmail(previousHighBidder, {
          title: auctionTitle,
          yourBid: parseFloat(bids?.[0]?.bid_amount || 0),
          currentBid: amount
        });
      }

      if (currentAuction?.seller_id && currentAuction?.seller_id !== user?.id) {
        auctionAlertService?.sendBidChangedAlert(currentAuction?.seller_id, {
          id: currentAuction?.id,
          title: auctionTitle,
          currentBid: amount,
          previousBid: parseFloat(currentAuction?.current_bid || currentAuction?.starting_bid || 0)
        });
      }

      liveNotificationService?.createNotification({
        userId: user?.id,
        notificationType: 'bid_placed',
        title: 'Bid placed successfully!',
        message: `Your bid of $${amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })} on "${auctionTitle}" is now the leading bid.`,
        auctionId: currentAuction?.id,
        metadata: { bidAmount: amount }
      });

      auctionEmailService?.sendBidPlacedEmail(user?.id, {
        title: auctionTitle,
        bidAmount: amount,
        endDate: currentAuction?.end_date
      });

      setBidSuccess(`Bid of $${amount?.toFixed(2)} placed successfully!`);
      setBidAmount('');
      loadBids();
      refreshAuction();
      onBidPlaced?.();
    } else {
      setBidError(error || 'Failed to place bid');
    }

    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
      <div className="bg-card border border-border rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto shadow-premium-lg animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Gavel" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-card-foreground leading-tight">{currentAuction?.title}</h2>
              <div className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                <span className="text-xs text-muted-foreground">
                  {currentAuction?.auction_type === 'premiere' ? 'Premiere Auction' : 'Live Auction'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Image + Details */}
          <div className="lg:col-span-2 space-y-4">
            <div className="aspect-square bg-muted rounded-xl overflow-hidden relative">
              {currentAuction?.featured_image_url || currentAuction?.collectible?.primary_image_url ? (
                <img
                  src={currentAuction?.featured_image_url || currentAuction?.collectible?.primary_image_url}
                  alt={currentAuction?.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="Image" size={48} className="text-muted-foreground" />
                </div>
              )}
              {currentAuction?.auction_type === 'premiere' && (
                <div className="absolute top-3 left-3 bg-yellow-500 text-white px-2.5 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
                  <Icon name="Star" size={11} />
                  <span>Premiere</span>
                </div>
              )}
            </div>

            <div className="bg-muted/20 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold text-card-foreground">Item Details</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{currentAuction?.description}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground block text-xs mb-0.5">Condition</span>
                  <span className="font-medium text-card-foreground capitalize">{currentAuction?.condition?.replace('-', ' ') || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-xs mb-0.5">Shipping</span>
                  <span className="font-medium text-card-foreground">${parseFloat(currentAuction?.shipping_cost || 0)?.toFixed(2)}</span>
                </div>
                {currentAuction?.category?.name && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground block text-xs mb-0.5">Category</span>
                    <span className="font-medium text-card-foreground">{currentAuction?.category?.name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Bidding + Feed */}
          <div className="lg:col-span-3 space-y-4">
            {/* Timer */}
            <div className={`rounded-xl p-4 flex items-center justify-between ${isEnded ? 'bg-destructive/10' : 'bg-primary/10'}`}>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={18} className={isEnded ? 'text-destructive' : 'text-primary'} />
                <span className="text-sm font-medium text-muted-foreground">Time Remaining</span>
              </div>
              <span className={`text-xl font-bold font-mono ${isEnded ? 'text-destructive' : 'text-primary'}`}>
                {timeRemaining || '—'}
              </span>
            </div>

            {/* Current Bid */}
            <div className="bg-card border-2 border-primary/30 rounded-xl p-5 text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Current Bid</p>
              <p className="text-4xl font-bold text-primary mb-1">
                ${parseFloat(currentAuction?.current_bid || currentAuction?.starting_bid || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-muted-foreground">
                {currentAuction?.bid_count || bids?.length || 0} bid{(currentAuction?.bid_count || bids?.length || 0) !== 1 ? 's' : ''} · Min next bid: <span className="text-foreground font-medium">${minBid?.toFixed(2)}</span>
              </p>
            </div>

            {/* Bid Form */}
            {!isEnded && !isOwnAuction && user && (
              <form onSubmit={handlePlaceBid} className="space-y-3">
                <div className="flex space-x-3">
                  <div className="flex-1">
                    <Input
                      type="number"
                      placeholder={`Min $${minBid?.toFixed(2)}`}
                      value={bidAmount}
                      onChange={(e) => { setBidAmount(e?.target?.value); setBidError(''); setBidSuccess(''); }}
                      min={minBid}
                      step="0.01"
                    />
                  </div>
                  <Button
                    type="submit"
                    loading={submitting}
                    disabled={submitting || !bidAmount}
                    iconName="Gavel"
                    iconPosition="left"
                    className="flex-shrink-0"
                  >
                    Place Bid
                  </Button>
                </div>
                {bidError && (
                  <div className="flex items-center space-x-2 text-destructive text-sm bg-destructive/10 px-3 py-2 rounded-lg">
                    <Icon name="AlertCircle" size={14} />
                    <span>{bidError}</span>
                  </div>
                )}
                {bidSuccess && (
                  <div className="flex items-center space-x-2 text-success text-sm bg-success/10 px-3 py-2 rounded-lg">
                    <Icon name="CheckCircle" size={14} />
                    <span>{bidSuccess}</span>
                  </div>
                )}
              </form>
            )}

            {isOwnAuction && (
              <div className="flex items-center space-x-2 text-muted-foreground text-sm bg-muted/30 px-4 py-3 rounded-lg">
                <Icon name="Info" size={14} />
                <span>You cannot bid on your own auction.</span>
              </div>
            )}

            {!user && (
              <div className="flex items-center space-x-2 text-muted-foreground text-sm bg-muted/30 px-4 py-3 rounded-lg">
                <Icon name="LogIn" size={14} />
                <span>Sign in to place a bid.</span>
              </div>
            )}

            {/* Live Bid Feed */}
            <LiveBidFeed bids={bids} newBidFlash={newBidFlash} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuctionFloorDetail;
